import { useCallback, useEffect, useRef, useState } from 'react';
import { store, useStore } from '@/state/store';
import { getImage } from '@/engine/pipeline';
import { renderDesign, blockBoundsPx } from '@/engine/render/renderer';
import { getFormat } from '@/lib/formats';
import { clamp } from '@/lib/utils';
import type { DesignSpec } from '@/types';

/**
 * The live canvas.
 *
 * Rendering happens at the format's native pixel size on a canvas that is then
 * CSS-scaled to fit. That keeps text crisp on high-DPI screens and means the
 * preview is pixel-identical to a 1x export.
 *
 * Direct manipulation is handled here rather than with DOM overlays so hit
 * testing uses exactly the same geometry the renderer draws with.
 */

type DragMode =
  | { kind: 'none' }
  | { kind: 'move'; blockId: string; dx: number; dy: number }
  | { kind: 'resize'; blockId: string; startSize: number; startY: number }
  | { kind: 'logo'; dx: number; dy: number };

export function CanvasEditor() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragMode>({ kind: 'none' });

  const spec = useStore((s) => s.variants.find((v) => v.id === s.activeId) ?? null);
  const photoUrl = useStore((s) => s.photoDataUrl);
  const logoUrl = useStore((s) => s.logoDataUrl);
  const analysis = useStore((s) => s.analysis);
  const showGuides = useStore((s) => s.showGuides);
  const showFaces = useStore((s) => s.showFaces);
  const selectedBlockId = useStore((s) => s.selectedBlockId);

  const [display, setDisplay] = useState({ w: 0, h: 0 });
  const [cursor, setCursor] = useState<'default' | 'grab' | 'grabbing' | 'nwse-resize'>('default');

  const format = getFormat(spec?.format ?? 'ig-portrait');

  // --- Render ------------------------------------------------------------
  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !spec) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = format.width;
    canvas.height = format.height;

    const photo = photoUrl ? await getImage(photoUrl) : null;
    const logo = logoUrl ? await getImage(logoUrl) : null;

    await renderDesign(ctx, spec, photo, logo, {
      width: format.width,
      height: format.height,
      includeImage: true,
      guides: {
        safe: showGuides ? format.safe : undefined,
        faces: showFaces ? (analysis?.faces ?? []) : undefined,
        selectedBlockId,
      },
    });
  }, [spec, photoUrl, logoUrl, format, showGuides, showFaces, analysis, selectedBlockId]);

  useEffect(() => {
    void draw();
  }, [draw]);

  // --- Fit to container --------------------------------------------------
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const resize = () => {
      const bounds = wrap.getBoundingClientRect();
      const scale = Math.min(
        bounds.width / format.width,
        bounds.height / format.height,
      );
      setDisplay({
        w: Math.max(1, format.width * scale),
        h: Math.max(1, format.height * scale),
      });
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [format]);

  // --- Hit testing -------------------------------------------------------
  const toCanvasCoords = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      return {
        x: ((event.clientX - rect.left) / rect.width) * format.width,
        y: ((event.clientY - rect.top) / rect.height) * format.height,
      };
    },
    [format],
  );

  const hitTest = useCallback(
    (px: number, py: number, current: DesignSpec) => {
      const handleSize = Math.max(14, format.width * 0.02);

      // Resize handle of the selected block wins, so a handle sitting on top of
      // another block stays grabbable.
      if (selectedBlockId) {
        const block = current.blocks.find((b) => b.id === selectedBlockId);
        if (block) {
          const b = blockBoundsPx(block, format.width, format.height);
          if (
            Math.abs(px - (b.x + b.w)) < handleSize &&
            Math.abs(py - (b.y + b.h)) < handleSize
          ) {
            return { type: 'resize' as const, id: block.id };
          }
        }
      }

      // Topmost block first — later blocks draw over earlier ones.
      for (let i = current.blocks.length - 1; i >= 0; i--) {
        const block = current.blocks[i];
        if (!block.visible || block.locked) continue;
        const b = blockBoundsPx(block, format.width, format.height);
        const pad = format.width * 0.012;
        if (px >= b.x - pad && px <= b.x + b.w + pad && py >= b.y - pad && py <= b.y + b.h + pad) {
          return { type: 'block' as const, id: block.id };
        }
      }

      if (current.logo?.visible && logoUrl) {
        const logo = current.logo;
        const w = logo.width * format.width;
        const h = w * 0.4; // Approximate: exact ratio needs the decoded image.
        const lx = logo.freePosition
          ? logo.x * format.width
          : logo.corner.endsWith('right')
            ? format.width - logo.padding * format.width - w
            : logo.padding * format.width;
        const ly = logo.freePosition
          ? logo.y * format.height
          : logo.corner.startsWith('bottom')
            ? format.height - logo.padding * format.height - h
            : logo.padding * format.height;
        if (px >= lx && px <= lx + w && py >= ly - h && py <= ly + h * 1.6) {
          return { type: 'logo' as const, id: 'logo' };
        }
      }

      return null;
    },
    [format, selectedBlockId, logoUrl],
  );

  // --- Pointer handlers --------------------------------------------------
  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!spec) return;
    const { x, y } = toCanvasCoords(event);
    const hit = hitTest(x, y, spec);

    if (!hit) {
      store.set({ selectedBlockId: null });
      dragRef.current = { kind: 'none' };
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);

    if (hit.type === 'resize') {
      const block = spec.blocks.find((b) => b.id === hit.id);
      if (!block) return;
      dragRef.current = {
        kind: 'resize',
        blockId: block.id,
        startSize: block.fontSize,
        startY: y,
      };
      setCursor('nwse-resize');
      return;
    }

    if (hit.type === 'logo' && spec.logo) {
      const logo = spec.logo;
      const lx = logo.freePosition
        ? logo.x * format.width
        : logo.corner.endsWith('right')
          ? format.width - logo.padding * format.width - logo.width * format.width
          : logo.padding * format.width;
      const ly = logo.freePosition ? logo.y * format.height : logo.padding * format.height;
      dragRef.current = { kind: 'logo', dx: x - lx, dy: y - ly };
      store.set({ selectedBlockId: null });
      setCursor('grabbing');
      return;
    }

    const block = spec.blocks.find((b) => b.id === hit.id);
    if (!block) return;
    store.set({ selectedBlockId: block.id });
    dragRef.current = {
      kind: 'move',
      blockId: block.id,
      dx: x - block.x * format.width,
      dy: y - block.y * format.height,
    };
    setCursor('grabbing');
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!spec) return;
    const { x, y } = toCanvasCoords(event);
    const drag = dragRef.current;

    if (drag.kind === 'none') {
      const hit = hitTest(x, y, spec);
      setCursor(hit ? (hit.type === 'resize' ? 'nwse-resize' : 'grab') : 'default');
      return;
    }

    if (drag.kind === 'move') {
      store.updateActive(
        (current) => ({
          ...current,
          blocks: current.blocks.map((b) =>
            b.id === drag.blockId
              ? {
                  ...b,
                  x: clamp((x - drag.dx) / format.width, -0.1, 1.1),
                  y: clamp((y - drag.dy) / format.height, -0.1, 1.1),
                }
              : b,
          ),
        }),
        `move-${drag.blockId}`,
      );
      return;
    }

    if (drag.kind === 'resize') {
      // Vertical drag scales the type; 400px of travel roughly doubles it.
      const delta = (y - drag.startY) / format.height;
      const next = clamp(drag.startSize * (1 + delta * 2.4), 0.012, 0.32);
      store.updateActive(
        (current) => ({
          ...current,
          blocks: current.blocks.map((b) =>
            b.id === drag.blockId ? { ...b, fontSize: next, autoLines: false } : b,
          ),
        }),
        `resize-${drag.blockId}`,
      );
      return;
    }

    if (drag.kind === 'logo') {
      store.updateActive(
        (current) => ({
          ...current,
          logo: current.logo
            ? {
                ...current.logo,
                freePosition: true,
                x: clamp((x - drag.dx) / format.width, 0, 1),
                y: clamp((y - drag.dy) / format.height, 0, 1),
              }
            : null,
        }),
        'move-logo',
      );
    }
  };

  const endDrag = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (dragRef.current.kind !== 'none') {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragRef.current = { kind: 'none' };
    setCursor('default');
  };

  // --- Keyboard nudge ----------------------------------------------------
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

      const id = store.getState().selectedBlockId;
      if (!id) return;
      const step = event.shiftKey ? 0.02 : 0.004;
      const deltas: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
        ArrowUp: [0, -step],
        ArrowDown: [0, step],
      };
      const delta = deltas[event.key];
      if (!delta) return;
      event.preventDefault();
      store.updateActive(
        (current) => ({
          ...current,
          blocks: current.blocks.map((b) =>
            b.id === id ? { ...b, x: b.x + delta[0], y: b.y + delta[1] } : b,
          ),
        }),
        `nudge-${id}`,
      );
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!spec) {
    return (
      <div
        ref={wrapRef}
        className="flex h-full w-full items-center justify-center rounded-xl border border-dashed border-white/10 bg-ink-900/40"
      >
        <div className="max-w-xs px-6 text-center">
          <p className="text-sm text-ink-300">No design yet</p>
          <p className="mt-1.5 text-[11px] leading-relaxed text-ink-500">
            Add a photo and a headline, then press{' '}
            <span className="text-accent">Generate designs</span>. Four
            compositions will appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="flex h-full w-full items-center justify-center">
      <canvas
        ref={canvasRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        style={{
          width: display.w ? `${display.w}px` : '100%',
          height: display.h ? `${display.h}px` : 'auto',
          cursor,
          touchAction: 'none',
        }}
        className="rounded-lg shadow-[0_24px_64px_-24px_rgba(0,0,0,0.9)]"
      />
    </div>
  );
}
